// Pure URL classifier for wiki links. Lives in its own module so both
// `useWikiMarkdown` (markdown-it link rule) and
// `useWikiMarkdown.citations` (citation-href matching) can import it
// without forming a circular dependency. Keeping the citations module
// importable from the renderer module — and the renderer needing the
// classifier too — was producing a Vite-evaluated module cycle that
// re-declared `injectInlineCitations` at runtime.

// Constructs a base URL from the current page's wiki-relative path,
// resolves the href against it, then extracts the pathname if the
// result stayed on our dummy host. Anything that escapes the dummy host
// (external link, mailto:, data:) is classified as external and gets
// new-tab-safe attributes instead of an SPA rewrite.
const DUMMY_HOST = 'http://wiki.local/'
const DUMMY_HOSTNAME = 'wiki.local'

export type LinkClassification =
  | { kind: 'wiki'; href: string } // internal .md → /p/<path>
  | { kind: 'external' } // http(s)/mailto/etc → new tab
  | { kind: 'inert' } // anchor-only, relative non-md, garbage

/**
 * Decides whether a link is wiki-internal (rewrite to `/p/<path>`),
 * external (open in a new tab), or inert (leave alone). Both the
 * markdown renderer's `link_open` rule and the test suite call this
 * directly; production click-handling reads the rewritten href off the
 * DOM, so this stays a single source of truth for "what does this link
 * mean."
 *
 * `knownPaths` (optional) lets the resolver recover when a writer (e.g.
 * the Ingest LLM) emits a bare filename like `anthropic.md` from inside
 * `log.md` — naive resolution gives `/p/anthropic.md` but the actual
 * page lives at `concepts/anthropic.md`. With the catalog in hand we
 * rewrite to the unambiguous suffix match.
 */
export function resolveWikiLink(href: string, currentPath: string, knownPaths?: readonly string[]): string | null {
  const classification = classifyLink(href, currentPath, knownPaths)
  return classification.kind === 'wiki' ? classification.href : null
}

export function classifyLink(href: string, currentPath: string, knownPaths?: readonly string[]): LinkClassification {
  if (!href) return { kind: 'inert' }
  if (href.startsWith('#')) return { kind: 'inert' }

  try {
    const base = new URL(currentPath || '', DUMMY_HOST)
    const resolved = new URL(href, base)

    if (resolved.hostname !== DUMMY_HOSTNAME) {
      // Anything that escapes the dummy host is a real external URL
      // (scheme-prefixed, or we resolved against a non-relative base).
      return { kind: 'external' }
    }

    const path = resolved.pathname.replace(/^\//, '')
    if (!/\.md$/i.test(path)) return { kind: 'inert' }

    const resolvedPath = resolveAgainstCatalog(path, knownPaths)
    return { kind: 'wiki', href: `/p/${resolvedPath}${resolved.hash || ''}` }
  } catch {
    return { kind: 'inert' }
  }
}

// If `path` is a bare filename that no catalog page actually owns, look
// for a single catalog entry ending in `/<filename>` and prefer that.
// Multiple matches stay ambiguous (leave the path as-is so we don't
// guess which folder the writer meant).
function resolveAgainstCatalog(path: string, knownPaths?: readonly string[]): string {
  if (!knownPaths || knownPaths.length === 0) return path
  if (path.includes('/')) return path
  if (knownPaths.includes(path)) return path

  const suffix = `/${path}`
  const matches = knownPaths.filter((p) => p.endsWith(suffix))
  return matches.length === 1 ? matches[0] : path
}
